/**
 * CampaignApprovalsPage — Founder review queue for pending marketing campaigns.
 *
 * Lists every campaign with status="pending". Founder reads the generated
 * content inline (landing page copy, blog, SEO, pricing) without navigating
 * away, then approves or rejects with a founder-signed action.
 *
 * Auth: X-Founder-Signature over JCS-canonical request body — same pattern
 * as MarketingCampaignDetailPage. Signing delegates to /admin/founder/sign-hash.
 *
 * Route: /omega/marketing/approvals
 */

import { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { signHashWithFounderKey } from '@/api/real/founderSign';
import { apiFetch, apiBaseUrl } from '@/api/http';
import { getAccessToken } from '@/api/supabase';

// ── Wire types ────────────────────────────────────────────────────────────────

interface CampaignSummary {
  id: string;
  product_id: string;
  status: string;
  omega_log_seq: number;
  created_at: number;
  executed_at: number | null;
  slug: string | null;
  title_tag: string | null;
}

interface ContentSummary {
  id: string;
  campaign_id: string;
  content_type: string;
  body: string;
  brand_voice_version: string;
  llm_response_hash: string | null;
  created_at: number;
}

interface ListCampaignsResponse {
  campaigns: CampaignSummary[];
  total: number;
}

interface CampaignDetailResponse {
  campaign: CampaignSummary;
  content: ContentSummary[];
}

// ── API ───────────────────────────────────────────────────────────────────────

function fetchCampaigns(): Promise<ListCampaignsResponse> {
  return apiFetch<ListCampaignsResponse>('/admin/marketing/campaigns');
}

function fetchDetail(id: string): Promise<CampaignDetailResponse> {
  return apiFetch<CampaignDetailResponse>(`/admin/marketing/campaigns/${id}`);
}

function sortKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(sortKeys);
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, sortKeys(v)]),
    );
  }
  return obj;
}

async function sha256hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function callCampaignAction(
  campaignId: string,
  action: 'approve' | 'reject',
  extra?: Record<string, unknown>,
): Promise<void> {
  const body: Record<string, unknown> = { nonce: crypto.randomUUID(), ...extra };
  const canonical = JSON.stringify(sortKeys(body));
  const hashHex = await sha256hex(canonical);
  const sig = await signHashWithFounderKey(hashHex);
  const token = await getAccessToken();
  const resp = await fetch(
    `${apiBaseUrl()}/admin/marketing/campaigns/${campaignId}/${action}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Founder-Signature': sig,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: canonical,
    },
  );
  if (!resp.ok) {
    const err = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `HTTP ${resp.status}`);
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseCampaignOutput(body: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(body) as unknown;
    if (v && typeof v === 'object' && !Array.isArray(v)) return v as Record<string, unknown>;
    return null;
  } catch {
    return null;
  }
}

// ── Content preview ───────────────────────────────────────────────────────────

function ContentPreview({ content, campaignId }: { content: ContentSummary[]; campaignId: string }) {
  const outputRow = content.find((c) => c.content_type === 'campaign_output');
  const parsed = outputRow ? parseCampaignOutput(outputRow.body) : null;
  const cid = outputRow?.id;

  if (!outputRow && content.length === 0) {
    return (
      <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8.5pt', fontStyle: 'italic', padding: '10px 0' }}>
        No content generated yet.
      </div>
    );
  }

  const panelStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 5,
    overflow: 'hidden',
  };

  const panelHeadStyle: React.CSSProperties = {
    padding: '7px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    background: 'rgba(255,255,255,0.02)',
    fontSize: '8pt',
    fontWeight: 700,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: 'var(--omega-ink)',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  };

  const panelBodyStyle: React.CSSProperties = {
    padding: '10px 12px',
    fontSize: '8.5pt',
    color: 'var(--omega-ink)',
    lineHeight: 1.55,
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {/* Landing Page */}
        <div style={panelStyle}>
          <div style={panelHeadStyle}>Landing Page</div>
          <div style={panelBodyStyle}>
            {parsed?.hero_h1 ? (
              <>
                <div style={{ fontWeight: 700, fontSize: '10pt', marginBottom: 5 }}>{String(parsed.hero_h1)}</div>
                {parsed.hero_subtitle && (
                  <div style={{ color: 'var(--omega-ink-subtle)', marginBottom: 7 }}>{String(parsed.hero_subtitle)}</div>
                )}
                {parsed.landing_page_md && (
                  <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8pt' }}>
                    {String(parsed.landing_page_md).slice(0, 200)}…
                  </div>
                )}
              </>
            ) : (
              <span style={{ fontStyle: 'italic', color: 'var(--omega-ink-subtle)' }}>Not generated.</span>
            )}
          </div>
        </div>

        {/* Blog Post */}
        <div style={panelStyle}>
          <div style={panelHeadStyle}>Blog Post</div>
          <div style={panelBodyStyle}>
            {parsed?.blog_post_md ? (
              <div style={{ color: 'var(--omega-ink-subtle)' }}>
                {String(parsed.blog_post_md).slice(0, 240)}…
              </div>
            ) : (
              <span style={{ fontStyle: 'italic', color: 'var(--omega-ink-subtle)' }}>Not generated.</span>
            )}
          </div>
        </div>

        {/* SEO */}
        <div style={panelStyle}>
          <div style={panelHeadStyle}>SEO</div>
          <div style={panelBodyStyle}>
            {parsed?.meta_description ? (
              <>
                <div style={{ marginBottom: 8 }}>{String(parsed.meta_description)}</div>
                {Array.isArray(parsed.keywords) && parsed.keywords.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {(parsed.keywords as unknown[]).slice(0, 8).map((k, i) => (
                      <span
                        key={i}
                        style={{
                          background: 'rgba(125,211,252,0.1)',
                          border: '1px solid rgba(125,211,252,0.25)',
                          borderRadius: 3,
                          color: '#7dd3fc',
                          fontSize: '7.5pt',
                          padding: '1px 6px',
                        }}
                      >
                        {String(k)}
                      </span>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <span style={{ fontStyle: 'italic', color: 'var(--omega-ink-subtle)' }}>Not generated.</span>
            )}
          </div>
        </div>

        {/* Pricing */}
        <div style={panelStyle}>
          <div style={panelHeadStyle}>Pricing</div>
          <div style={panelBodyStyle}>
            {parsed?.price_usd_cents !== undefined ? (
              <>
                <div style={{ fontSize: '15pt', fontWeight: 700, color: '#6ee7b7', marginBottom: 4 }}>
                  ${((parsed.price_usd_cents as number) / 100).toFixed(2)}
                  <span style={{ fontSize: '9pt', color: 'var(--omega-ink-subtle)', marginLeft: 4 }}>/mo</span>
                </div>
                {parsed.tier_name != null && (
                  <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>Tier: {String(parsed.tier_name)}</div>
                )}
              </>
            ) : (
              <span style={{ fontStyle: 'italic', color: 'var(--omega-ink-subtle)' }}>Not generated.</span>
            )}
          </div>
        </div>
      </div>

      {/* Link to full edit page */}
      <div style={{ marginTop: 10, textAlign: 'right' }}>
        <Link
          to={`/omega/marketing/campaigns/${campaignId}`}
          style={{ fontSize: '8pt', color: '#7dd3fc', textDecoration: 'none', border: '1px solid rgba(125,211,252,0.3)', borderRadius: 3, padding: '3px 10px', letterSpacing: '0.04em' }}
        >
          View / Edit full detail
        </Link>
        {cid && (
          <Link
            to={`/omega/marketing/content/${cid}`}
            style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', textDecoration: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 3, padding: '3px 10px', letterSpacing: '0.04em', marginLeft: 8 }}
          >
            Edit content
          </Link>
        )}
      </div>
    </div>
  );
}

// ── Per-campaign approval row ─────────────────────────────────────────────────

interface ApprovalRowProps {
  campaign: CampaignSummary;
  onApprove: () => void;
  onReject: (reason: string) => void;
  busy: boolean;
}

function ApprovalRow({ campaign: c, onApprove, onReject, busy }: ApprovalRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState('');

  const detail = useQuery({
    queryKey: ['marketing', 'campaign', c.id],
    queryFn: () => fetchDetail(c.id),
    enabled: expanded,
    staleTime: 60_000,
  });

  function confirmReject() {
    onReject(reason);
    setRejectMode(false);
    setReason('');
  }

  const btnBase: React.CSSProperties = {
    fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt',
    fontWeight: 600,
    border: 'none',
    borderRadius: 4,
    padding: '5px 14px',
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };

  return (
    <div
      style={{
        borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
        padding: '12px 0',
      }}
    >
      {/* Row header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '10pt', fontWeight: 700, color: 'var(--omega-ink)' }}>
              {c.title_tag ?? c.slug ?? c.product_id}
            </span>
            {c.slug && (
              <span style={{ fontSize: '8pt', color: '#7dd3fc' }}>/{c.slug}</span>
            )}
          </div>
          <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)' }}>
            Created {fmtDate(c.created_at)}
            <span style={{ margin: '0 6px', opacity: 0.4 }}>·</span>
            product: {c.product_id.slice(0, 20)}{c.product_id.length > 20 ? '…' : ''}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <button
            onClick={() => setExpanded((e) => !e)}
            style={{ ...btnBase, background: 'rgba(255,255,255,0.06)', color: 'var(--omega-ink)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            {expanded ? 'Hide content ▲' : 'Preview content ▼'}
          </button>
          {!rejectMode && (
            <>
              <button
                onClick={onApprove}
                disabled={busy}
                style={{ ...btnBase, background: '#6ee7b7', color: '#000' }}
              >
                {busy ? 'Signing…' : 'Approve'}
              </button>
              <button
                onClick={() => setRejectMode(true)}
                disabled={busy}
                style={{ ...btnBase, background: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
              >
                Reject
              </button>
            </>
          )}
        </div>
      </div>

      {/* Reject input */}
      {rejectMode && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <input
            autoFocus
            placeholder="Rejection reason (optional)…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setRejectMode(false); setReason(''); }
              if (e.key === 'Enter') { confirmReject(); }
            }}
            style={{
              flex: 1,
              minWidth: 200,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(248,113,113,0.3)',
              borderRadius: 4,
              color: 'var(--omega-ink)',
              fontFamily: 'var(--omega-mono)',
              fontSize: '8.5pt',
              padding: '5px 10px',
              outline: 'none',
            }}
          />
          <button
            onClick={confirmReject}
            disabled={busy}
            style={{ ...btnBase, background: '#f87171', color: '#000' }}
          >
            Confirm Reject
          </button>
          <button
            onClick={() => { setRejectMode(false); setReason(''); }}
            style={{ ...btnBase, background: 'rgba(255,255,255,0.06)', color: 'var(--omega-ink)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Inline content preview (loaded on expand) */}
      {expanded && (
        <div style={{ marginTop: 14, paddingLeft: 2 }}>
          {detail.isPending && (
            <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '8.5pt' }}>Loading content…</div>
          )}
          {detail.isError && (
            <div style={{ color: '#f87171', fontSize: '8.5pt' }}>Failed to load content.</div>
          )}
          {detail.data && (
            <ContentPreview content={detail.data.content} campaignId={c.id} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────

interface ToastState { text: string; ok: boolean }

function useToast() {
  const [msg, setMsg] = useState<ToastState | null>(null);
  function show(text: string, ok: boolean) {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 4000);
  }
  return { msg, show };
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function CampaignApprovalsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const qc = useQueryClient();
  const { msg: toastMsg, show: showToast } = useToast();

  const { data, isPending, isError } = useQuery({
    queryKey: ['marketing', 'campaigns'],
    queryFn: fetchCampaigns,
    refetchInterval: 30_000,
  });

  const pending = (data?.campaigns ?? []).filter((c) => c.status === 'pending');

  const actionMutation = useMutation({
    mutationFn: ({
      id,
      action,
      reason,
    }: {
      id: string;
      action: 'approve' | 'reject';
      reason?: string;
    }) => callCampaignAction(id, action, reason ? { reason } : undefined),
    onSuccess: (_data, variables) => {
      showToast(
        variables.action === 'approve' ? 'Approved — will publish once W2 distribution is wired.' : 'Rejected.',
        true,
      );
      void qc.invalidateQueries({ queryKey: ['marketing', 'campaigns'] });
    },
    onError: (err: Error) => showToast(err.message || 'Action failed.', false),
  });

  const busy = actionMutation.isPending;

  return (
    <div
      className="omega-shell"
      style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}
    >
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Marketing', to: '/omega/marketing' },
          { label: 'Approvals' },
        ]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, marginTop: 8 }}>
        <h1
          style={{
            margin: 0,
            fontSize: '11pt',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Campaign Approvals
        </h1>
        {data && (
          <span
            style={{
              background: pending.length > 0 ? 'rgba(125,211,252,0.1)' : 'rgba(255,255,255,0.06)',
              border: `1px solid ${pending.length > 0 ? 'rgba(125,211,252,0.3)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: 3,
              fontSize: '8pt',
              padding: '1px 7px',
              color: pending.length > 0 ? '#7dd3fc' : 'var(--omega-ink-subtle)',
            }}
          >
            {pending.length} pending
          </span>
        )}
      </div>

      <p style={{ margin: '0 0 18px', fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', lineHeight: 1.55 }}>
        Preview each campaign's generated content — landing page, blog, SEO, pricing — then
        Approve or Reject. Approve sets status to "approved"; once W2's distribution pipeline
        is wired, approved campaigns post automatically.
      </p>

      {isPending && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading campaigns…</div>
      )}
      {isError && (
        <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load campaigns.</div>
      )}

      {!isPending && !isError && pending.length === 0 && (
        <div
          style={{
            border: '1px dashed rgba(255,255,255,0.1)',
            borderRadius: 6,
            padding: '40px 24px',
            textAlign: 'center',
            color: 'var(--omega-ink-subtle)',
            fontSize: '9pt',
          }}
        >
          No pending campaigns. OMEGA has not generated any new campaigns awaiting approval.
          <div style={{ marginTop: 10 }}>
            <Link
              to="/omega/marketing/campaigns"
              style={{ color: '#7dd3fc', textDecoration: 'none', fontSize: '8.5pt' }}
            >
              View all campaigns
            </Link>
          </div>
        </div>
      )}

      {pending.map((c) => (
        <ApprovalRow
          key={c.id}
          campaign={c}
          busy={busy}
          onApprove={() => actionMutation.mutate({ id: c.id, action: 'approve' })}
          onReject={(reason) => {
              if (reason) {
                actionMutation.mutate({ id: c.id, action: 'reject', reason });
              } else {
                actionMutation.mutate({ id: c.id, action: 'reject' });
              }
            }}
        />
      ))}

      {/* Toast */}
      {toastMsg && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 100,
            padding: '10px 20px',
            borderRadius: 8,
            background: toastMsg.ok ? '#065f46' : '#7f1d1d',
            color: '#fff',
            fontSize: '9pt',
            fontWeight: 600,
            boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
            whiteSpace: 'nowrap',
          }}
        >
          {toastMsg.text}
        </div>
      )}
    </div>
  );
}
